import React, { useState, useEffect, useContext, useRef } from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { render } from "@rakered/email";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MuiAlert from "@mui/material/Alert";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

// My Files
import BackButton from "../../BackButton";
import { DatabaseContext, UserContext } from "../../../context";
import firebase, { config } from "../../../firebase";
import UserPermissionsDialog from "./UserPermissionsDialog";
import AvatarUploadButton from "../AvatarUploadButton";
import ToolbarWithLogo from "../../ToolbarWithLogo";
import NoPermissionsMessage from "../../NoPermissionsMessage";
import ConsentFormEmail from "../emails/ConsentFormEmail";
import SetPasswordEmail from "../emails/SetPasswordEmail";
import ConsentFormAndPasswordEmail from "../emails/ConsentFormAndPasswordEmail";
import UserAvatar from "../UserAvatar";
import ManageHubEnrolementDialog from "./ManageHubEnrolementDialog";
import AccountRecoveryDialog from "./AccountRecoveryDialog";
import DuplicateUserDialog from "./DuplicateUserDialog";
import { makeid } from "../../../utility";
import { planSystemManagerChange } from "../../admin/systemManagerList";
import ManageGroupEnrolementDialog from "./ManageGroupEnrolementDialog";
import ConfirmDeleteDialog from "../../ConfirmDeleteDialog";
import { userDeletionUpdates } from "./userDeletion";
import DialogWithMessage from "../../DialogWithMessage";
import {
    errorHoverTint,
    errorTint,
    primaryHoverBorder,
    primaryTint,
    primaryTintBorder,
    warningBorder,
    warningSurface,
} from "../../../surfaceTints";
import {
    buildAdminUpdates,
    buildMembershipUpdates,
    createHubDraft,
    resolveUserAdmins,
} from "./userDraftPersistence";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

export default function NewUserPage() {
    const location = useLocation();

    const history = useHistory();

    const [user, setUser] = useState({}); // Use this to pass the avatarEditor between components, and also to null the avatar when it's been changed

    const [key, setKey] = useState("newuser");
    // Personal data for the user being EDITED. It lives on
    // /userprofiles, which the users list will stop carrying at
    // Stage 3 — without this the form would load blanks and then
    // save them back over someone's details.
    const [editedProfile, setEditedProfile] = useState(null);
    // A refused profile read is not the same as an empty profile. Saving on
    // top of one would write the form's blanks over real details, so the
    // save is blocked outright when it happens.
    const [editedProfileFailed, setEditedProfileFailed] = useState(false);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [under18, setUnder18] = useState(false);
    const [role, setRole] = useState("");
    const [customRoleName, setCustomRoleName] = useState("");

    const [parentFirstName, setParentFirstName] = useState("");
    const [parentLastName, setParentLastName] = useState("");
    const [parentEmail, setParentEmail] = useState("");
    const [parentPhone, setParentPhone] = useState("");
    const [imageURL, setImageURL] = useState("");

    const [isEditing, setIsEditing] = useState(false);

    const [permissions, setPermissions] = useState({});

    const [deleteIsInProgress, setDeleteIsInProgress] = useState(false);

    const [userCanDelete, setUserCanDelete] = useState(false);
    const [isInProgress, setIsInProgress] = useState(false);
    const [statusType, setStatusType] = useState("error");
    const [statusMessage, setStatusMessage] = useState("");
    const [statusIsVisible, setStatusIsVisible] = useState(false);
    // Creating a user swaps the matched route, which remounts this page, so the
    // confirmation is carried across the navigation in the location state.
    const [saveConfirmationIsVisible, setSaveConfirmationIsVisible] = useState(
        Boolean(location.state?.justCreated)
    );
    const [justCreatedUser, setJustCreatedUser] = useState(
        Boolean(location.state?.justCreated)
    );
    const [hasChangedAvatar, setHasChangedAvatar] = useState(false);

    const [permissionsDialogIsOpen, setPermissionsDialogIsOpen] =
        useState(false);

    const [duplicateUserDialogIsOpen, setDuplicateUserDialogIsOpen] =
        useState(false);

    const [accountRecoveryDialogIsOpen, setAccountRecoveryDialogIsOpen] =
        useState(false);
    const [hubDialogIsOpen, setHubDialogIsOpen] = useState(false);
    const [groupDialogIsOpen, setGroupDialogIsOpen] = useState(false);

    const {
        users,
        userLogins,
        files,
        folders,
        comments,
        conversations,
        groups,
        userConsent,
    } = useContext(DatabaseContext);

    const [userCanView, setUserCanView] = useState();

    const { currentUserData } = useContext(UserContext);

    const [hubs, setHubs] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);

    const [duplicateUser, setDuplicateUser] = useState(); // Used if the email address is already in use, we find the matching user and display their detils to see if it's the person they were trying to add

    const [editingHub, setEditingHub] = useState();

    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);

    const [hasGivenConsent, setHasGivenConsent] = useState();
    const [lastLogin, setLastLogin] = useState("");

    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [messageDialogString, setMessageDialogString] = useState("");
    const [messageDialogTitle, setMessageDialogTitle] = useState("");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const hasUnsavedChangesRef = useRef(false);
    const allowNavigationRef = useRef(false);

    const dialogIsOpen =
        permissionsDialogIsOpen ||
        duplicateUserDialogIsOpen ||
        accountRecoveryDialogIsOpen ||
        hubDialogIsOpen ||
        groupDialogIsOpen ||
        confirmDeleteDialogOpen ||
        messageDialogOpen ||
        saveConfirmationIsVisible;

    const unsavedChangesMessage =
        "You have unsaved changes. Leave this page without saving them?";

    function markUnsavedChanges() {
        allowNavigationRef.current = false;
        setSaveConfirmationIsVisible(false);

        if (!hasUnsavedChangesRef.current) {
            hasUnsavedChangesRef.current = true;
            setHasUnsavedChanges(true);
        }
    }

    function finishSaving() {
        allowNavigationRef.current = true;
        hasUnsavedChangesRef.current = false;
        setHasUnsavedChanges(false);
    }

    function discardChanges() {
        allowNavigationRef.current = true;
        hasUnsavedChangesRef.current = false;
        window.location.reload();
    }

    function goBackToUsers() {
        setSaveConfirmationIsVisible(false);
        history.push("/users");
    }

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (
                hasUnsavedChangesRef.current &&
                !allowNavigationRef.current
            ) {
                event.preventDefault();
                event.returnValue = "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    useEffect(() => {
        // Consume the flag once so the confirmation doesn't come back on a
        // reload or when the user navigates back to this entry.
        if (location.state?.justCreated) {
            history.replace(location.pathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // detaching the listener
            if (!user) {
                // If no user is logged in, redirect to sign in page
                // history.replace("/signin");
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (currentUserData && currentUserData.permissions) {
            setUserCanView(true);

            if (location.pathname.includes("newuser")) {
                // Creating a user, set default permissions
                if (currentUserData.permissions.addEditUsers) {
                    // For coach educators, default user should be Coach Ed Student
                    if (currentUserData.role === "FAW Coach Educator") {
                        let newUserRole = "";
                        if (role === "") {
                            newUserRole = "FAW Coach Ed Student";
                            setRole(newUserRole);
                            loadPermissionTemplate(newUserRole);
                        } else {
                            newUserRole = role;
                        }

                        setUser((prev) => ({
                            ...prev,
                            role: newUserRole,
                            key: "newuser",
                        }));
                    }
                    if (currentUserData.role === "Club Coach Educator") {
                        let newUserRole = "";
                        if (role === "") {
                            newUserRole = "Club Coach Ed Student";
                            setRole(newUserRole);
                            loadPermissionTemplate(newUserRole);
                        } else {
                            newUserRole = role;
                        }

                        setUser((prev) => ({
                            ...prev,
                            role: newUserRole,
                            key: "newuser",
                        }));
                    } else if (isClubUser()) {
                        let newUserRole = "";
                        if (role === "") {
                            newUserRole = "Club Player";
                            setRole(newUserRole);
                            loadPermissionTemplate(newUserRole);
                        } else {
                            newUserRole = role;
                        }

                        setUser((prev) => ({
                            ...prev,
                            role: newUserRole,
                            key: "newuser",
                        }));
                    } else if (
                        isFAWUser() ||
                        currentUserData.role === "System Digital Manager"
                    ) {
                        let newUserRole = "";
                        if (role === "") {
                            newUserRole = "FAW Player";
                            setRole(newUserRole);
                            loadPermissionTemplate(newUserRole);
                        } else {
                            newUserRole = role;
                        }

                        setUser((prev) => ({
                            ...prev,
                            role: newUserRole,
                            key: "newuser",
                        }));
                    } else {
                        let newUserRole = "";
                        if (role === "") {
                            newUserRole = "FAW Player";
                            setRole(newUserRole);
                            loadPermissionTemplate(newUserRole);
                        } else {
                            newUserRole = role;
                        }

                        setUser((prev) => ({
                            ...prev,
                            role: newUserRole,
                            key: "newuser",
                        }));
                    }

                    if (!hasUnsavedChangesRef.current) {
                        const hubDrafts = folders
                            .filter((folder) => folder.hub)
                            .map((folder) =>
                                createHubDraft(folder, groups),
                            );

                        setHubs(hubDrafts);

                        // By Default the user should be added to the Public Tutorial Videos Folder
                        setSelectedHubs(
                            hubDrafts.filter(
                                (folder) =>
                                    folder.key === "RhDWyGntB2ZjyoI1HEj" ||
                                    folder.ancestors.includes(
                                        "RhDWyGntB2ZjyoI1HEj",
                                    ),
                            ),
                        );
                    }
                } else {
                    history.goBack();
                    return;
                }
            } else {
                // Editing a user, load their data here

                let key = location.pathname.replace("/manageuser/", "");

                let contextUser = users.find((u) => u.key === key);

                // Wait for the profile before populating: filling the form
                // from a half-loaded record and then saving would overwrite
                // real details with blanks.
                if (contextUser && editedProfile === null) {
                    return;
                }

                let matchingUser = contextUser && {
                    ...contextUser,
                    ...(editedProfile || {}),
                };

                if (matchingUser) {
                    setIsEditing(true);

                    var canView = false;
                    var canDelete = false;

                    if (currentUserData.permissions.addEditUsers) {
                        if (currentUserData.role === "System Digital Manager") {
                            canView = true;
                            canDelete = true;
                        } else if (
                            matchingUser.createdBy === currentUserData.key
                        ) {
                            canView = true;
                            canDelete = true;
                        } else if (
                            matchingUser.admins &&
                            matchingUser.admins.includes(currentUserData.key)
                        ) {
                            canView = true;
                            canDelete = true;
                        }
                    }

                    setUserCanDelete(canDelete);
                    setUserCanView(canView);

                    if (!canView) {
                        history.goBack();
                        return;
                    }

                    if (email === "") {
                        setKey(matchingUser.key);
                        setFirstName(matchingUser.firstName);
                        setLastName(matchingUser.lastName);
                        setEmail(matchingUser.email);
                        setPhone(matchingUser.phone);
                        setImageURL(matchingUser.imageURL);
                        setRole(matchingUser.role);
                        setCustomRoleName(matchingUser.customRoleName);

                        setParentFirstName(matchingUser.parentFirstName);
                        setParentLastName(matchingUser.parentLastName);
                        setParentEmail(matchingUser.parentEmail);
                        setParentPhone(matchingUser.parentPhone);

                        setUser({ ...matchingUser });

                        if (matchingUser.under18) {
                            setUnder18(true);
                        } else {
                            setUnder18(false);
                        }

                        let matchingLogin = userLogins.find(
                            (l) => l.key === matchingUser.key
                        );

                        if (matchingLogin) {
                            setLastLogin(matchingLogin.lastLogin);
                        }

                        let matchingConsent = userConsent.find(
                            (c) => c.key === matchingUser.key
                        );
                        if (matchingConsent) {
                            if (matchingConsent.hasConsent) {
                                setHasGivenConsent(true);
                            } else {
                                setHasGivenConsent(false);
                            }
                        }
                    }

                    if (!hasUnsavedChangesRef.current) {
                        // Build isolated hub/group drafts so editing this form
                        // cannot mutate the realtime context before save.
                        const hubsArray = folders
                            .filter((folder) => folder.hub)
                            .map((folder) =>
                                createHubDraft(folder, groups),
                            );
                        const selectedHubsArray = hubsArray.filter((folder) =>
                            folder.users.includes(matchingUser.key),
                        );

                        setHubs(hubsArray);
                        setSelectedHubs(selectedHubsArray);
                    }

                    if (matchingUser.permissions) {
                        let permissions = {
                            addEditUsers: matchingUser.permissions.addEditUsers,
                            uploadEditOwnVideo:
                                matchingUser.permissions.uploadEditOwnVideo,
                            comment: matchingUser.permissions.comment,
                            openMessage: matchingUser.permissions.openMessage,
                            personalMessage:
                                matchingUser.permissions.personalMessage,
                            viewCalendar: matchingUser.permissions.viewCalendar,
                            editCalendar: matchingUser.permissions.editCalendar,
                            exportCalendar:
                                matchingUser.permissions.exportCalendar,
                            createOwnPlaylist:
                                matchingUser.permissions.createOwnPlaylist,
                            analysis: matchingUser.permissions.analysis,
                            createSurveys:
                                matchingUser.permissions.createSurveys,
                            shareFiles: matchingUser.permissions.shareFiles,
                            createProjects:
                                matchingUser.permissions.createProjects,
                        };

                        if (email === "") {
                            setPermissions(permissions);
                        }
                    }
                }
            }
        }

        return () => unsubscribe();
        // Don't put currentUserData in the dependancies, breaks the hub management dialog
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        user.permissions,
        location.pathname,
        user.createdBy,
        history,
        folders,
        groups,
        users,
        currentUserData,
        editedProfile,
    ]);

    const editedUserKey = location.pathname.startsWith("/manageuser/")
        ? location.pathname.replace("/manageuser/", "")
        : null;

    useEffect(() => {
        if (!editedUserKey || editedUserKey === "newuser") {
            setEditedProfile(null);
            setEditedProfileFailed(false);
            return;
        }

        let cancelled = false;
        setEditedProfileFailed(false);

        firebase
            .database()
            .ref("userprofiles")
            .child(editedUserKey)
            .once("value")
            .then((snapshot) => {
                if (!cancelled) {
                    setEditedProfile(snapshot.val() || {});
                }
            })
            .catch((error) => {
                // Show the form with an empty profile rather than blocking
                // it. Saving is what would lose data, and editUser refuses
                // while editedProfileFailed is set.
                console.log("Could not load the user's profile", error);
                if (!cancelled) {
                    setEditedProfile({});
                    setEditedProfileFailed(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [editedUserKey]);

    function loadPermissionTemplate(newRole) {
        var addEditUsers = false;
        var uploadEditOwnVideo = false;
        var comment = false;
        var openMessage = false;
        var personalMessage = false;
        var viewCalendar = false;
        var editCalendar = false;
        var exportCalendar = false;
        var createOwnPlaylist = false;
        var analysis = false;
        var createSurveys = false;
        var shareFiles = false;
        var createProjects = true;

        if (newRole === "System Digital Manager") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "FAW Team Ops") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "FAW Analyst") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "FAW Coach") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
        } else if (newRole === "FAW Support Staff") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "FAW Coach Educator") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
        } else if (newRole === "FAW Coach Ed Student") {
            comment = true;
            openMessage = true;
            viewCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createProjects = false;
        } else if (newRole === "FAW Player") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            viewCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
        } else if (newRole === "FAW Parent") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            exportCalendar = true;
        } else if (newRole === "Club Digital Manager") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "Club Administrator") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "Club Analyst") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "Club Coach") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
        } else if (newRole === "Club Coach Educator") {
            addEditUsers = true;
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
        } else if (newRole === "Club Coach Ed Student") {
            comment = true;
            openMessage = true;
            viewCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createProjects = false;
        } else if (newRole === "Club Player") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            viewCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
        } else if (newRole === "Club Parent") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            exportCalendar = true;
        } else if (newRole === "Club Support Staff") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            editCalendar = true;
            exportCalendar = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "Referee") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            viewCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
            shareFiles = true;
        } else if (newRole === "Referee Assessor") {
            uploadEditOwnVideo = true;
            comment = true;
            openMessage = true;
            personalMessage = true;
            viewCalendar = true;
            exportCalendar = true;
            createOwnPlaylist = true;
            analysis = true;
            createSurveys = true;
            shareFiles = true;
        }

        let newPermissions = {
            addEditUsers: addEditUsers,
            uploadEditOwnVideo: uploadEditOwnVideo,
            comment: comment,
            openMessage: openMessage,
            personalMessage: personalMessage,
            viewCalendar: viewCalendar,
            editCalendar: editCalendar,
            exportCalendar: exportCalendar,
            createOwnPlaylist: createOwnPlaylist,
            analysis: analysis,
            createSurveys: createSurveys,
            shareFiles: shareFiles,
            createProjects: createProjects,
        };

        setPermissions(newPermissions);
    }

    function handleChange(event) {
        let newValue = event.target.value;
        markUnsavedChanges();

        if (event.target.id === "firstName") {
            setFirstName(newValue);
        } else if (event.target.id === "lastName") {
            setLastName(newValue);
        } else if (event.target.id === "email") {
            setEmail(newValue);
        } else if (event.target.id === "phone") {
            setPhone(newValue);
        } else if (event.target.id === "parentEmail") {
            setParentEmail(newValue);
        } else if (event.target.id === "parentPhone") {
            setParentPhone(newValue);
        } else if (event.target.id === "parentFirstName") {
            setParentFirstName(newValue);
        } else if (event.target.id === "parentLastName") {
            setParentLastName(newValue);
        } else if (event.target.id === "role") {
            setRole(newValue);
            loadPermissionTemplate(event.target.value);
        } else if (event.target.id === "customRoleName") {
            setCustomRoleName(newValue);
        }
    }

    const handleStatusClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setStatusIsVisible(false);
    };

    const handleOpenPermissionsDialog = () => {
        setPermissionsDialogIsOpen(true);
    };

    const handleClosePermissionsDialog = (newPermissions) => {
        if (newPermissions) {
            if (
                JSON.stringify(newPermissions) !== JSON.stringify(permissions)
            ) {
                markUnsavedChanges();
            }
            setPermissions(newPermissions);
        }
        setPermissionsDialogIsOpen(false);
    };

    function handleSaveClick() {
        if (permissions === undefined) {
            setMessageDialogTitle("Cannot Save User");
            setMessageDialogString("Please select a role for the new user");
            setMessageDialogOpen(true);
            return;
        }

        if (firstName === "" || lastName === "") {
            setMessageDialogTitle("Cannot Save User");
            setMessageDialogString(
                "You need to set a First Name and Last Name for the User"
            );
            setMessageDialogOpen(true);
            return;
        }

        if (under18) {
            if (parentFirstName === "" || parentLastName === "") {
                setMessageDialogTitle("Cannot Save User");
                setMessageDialogString(
                    "You need to set a First Name and Last Name for the Parent / Carer / Guardian"
                );
                setMessageDialogOpen(true);
                return;
            }
        }

        if (isEditing) {
            editUser();
        } else {
            createNewUser();
        }
    }

    function sendConsentAndPasswordEmail(userKey) {
        // Joint email where the user gives consent, then creates their password
        var sendMail = firebase.functions().httpsCallable("sendMail");

        var consentPath = "userconsent";

        if (role === "FAW Coach Educator" || role === "FAW Coach Ed Student") {
            consentPath = "coachedconsent";
        } else if (role.includes("Club")) {
            consentPath = "clubuserconsent";
        }

        sendMail({
            dest: email,
            sender: "Nodiant Online <no.reply.nodiant@gmail.com>",
            subject: "Nodiant Online Consent Form",
            html: render(
                <ConsentFormAndPasswordEmail
                    userKey={userKey}
                    path={consentPath}
                    email={email}
                />
            ),
        })
            .then(() => {
                console.log("Combined Consent and Password Email Sent");
            })
            .catch((error) => {
                // Getting the Error details.
                console.log("Error");
                console.log(error.code);
                console.log(error.message);
                console.log(error.details);
            });
    }

    // For Under 18 users
    function sendConsentEmail(userKey, parentOrUser) {
        var dest = "";

        if (parentOrUser === "parent") {
            dest = parentEmail;
        } else {
            dest = email;
        }

        var consentPath = "consent";

        if (role.includes("Club")) {
            consentPath = "clubconsent";
        }

        var sendMail = firebase.functions().httpsCallable("sendMail");

        sendMail({
            dest: dest,
            sender: "Nodiant Online <no.reply.nodiant@gmail.com>",
            subject: "Nodiant Online Consent Form",
            html: render(
                <ConsentFormEmail
                    userKey={userKey}
                    path={consentPath}
                    userName={
                        parentOrUser === "parent"
                            ? firstName + " " + lastName
                            : ""
                    }
                />
            ),
        })
            .then(() => {
                console.log("Consent Email Sent");
            })
            .catch((error) => {
                // Getting the Error details.
                console.log("Error");
                console.log(error.code);
                console.log(error.message);
                console.log(error.details);
            });
    }

    function sendPasswordEmail(link) {
        var sendMail = firebase.functions().httpsCallable("sendMail");

        sendMail({
            dest: email,
            sender: "Nodiant Online <no.reply.nodiant@gmail.com>",
            subject: "Choose your Nodiant Online Password",
            html: render(
                <SetPasswordEmail
                    link={link}
                    message="Your password has been reset on the Nodiant Online Platform"
                />
            ),
        })
            .then(() => {
                console.log(`Password Email Sent to ${email}`);
            })
            .catch((error) => {
                console.error("Could not send password email", {
                    destinationEmail: email,
                    code: error.code,
                    message: error.message,
                    details: error.details,
                    error,
                });
            });
    }

    // After save has been pressed
    async function createNewUser() {
        setIsInProgress(true);
        const secondaryFirebase = firebase.initializeApp(config, makeid(15));
        const password = makeid(15);

        try {
            const userCredential = await secondaryFirebase
                .auth()
                .createUserWithEmailAndPassword(email, password);
            const userKey = userCredential.user.uid;
            const downloadURL = await uploadAvatar(userKey);
            const roleName = customRoleName || role;
            // Personal data and permissions live only on /userprofiles.
            const profileFields = {
                email,
                phone,
                parentFirstName,
                parentLastName,
                parentEmail,
                parentPhone,
                under18,
                permissions,
            };
            const updates = {
                [`users/${userKey}`]: {
                    uid: userKey,
                    firstName,
                    lastName,
                    imageURL: downloadURL,
                    role,
                    customRoleName: roleName,
                    admins: getAdminsObject(),
                    createdBy: currentUserData.key,
                },
                [`userprofiles/${userKey}`]: profileFields,
                [`userlogin/${userKey}`]: { lastLogin: "never" },
                [`userconsent/${userKey}`]: {
                    uid: userKey,
                    name: under18
                        ? parentFirstName + " " + parentLastName
                        : firstName + " " + lastName,
                    hasConsent: false,
                },
                ...buildMembershipUpdates({
                    folders,
                    groups,
                    selectedHubs,
                    savedUserKey: userKey,
                    draftUserKey: key,
                }),
            };

            await firebase.database().ref().update(updates);
            await syncSystemManager(userKey, undefined, role);
            setKey(userKey);

            if (under18) {
                sendConsentEmail(userKey, "parent");

                const generateEmailActionLink = firebase
                    .functions()
                    .httpsCallable("generateEmailActionLink");
                generateEmailActionLink({ email }).then((result) => {
                    sendPasswordEmail(result.data.link);
                });
            } else {
                sendConsentAndPasswordEmail(userKey);
            }

            finishSaving();
            setUser({
                ...updates[`users/${userKey}`],
                // The form carries on editing this user, so it needs the
                // personal half too, as it would after loading the profile.
                ...profileFields,
                // DatabaseContext hands admins out as keys, not a lookup.
                admins: Object.keys(updates[`users/${userKey}`].admins),
                key: userKey,
            });
            setImageURL(downloadURL);
            setHasChangedAvatar(false);
            setIsEditing(true);
            setJustCreatedUser(true);
            setSaveConfirmationIsVisible(true);
            history.replace(`/manageuser/${userKey}`, { justCreated: true });
        } catch (error) {
            const errorCode = error?.code;
            const errorMessage = error?.message || "Unable to create user";

            if (errorCode === "auth/email-already-in-use") {
                // Emails are no longer readable client-side, so the owner of
                // this address is resolved server-side. role and admins are
                // not personal data and still come from the users list; the
                // address is the one just typed, by definition.
                let matchingUser = null;

                try {
                    const lookup = await firebase
                        .functions()
                        .httpsCallable("checkEmailExists")({ email });

                    if (lookup.data && lookup.data.exists) {
                        const existing = users.find(
                            (u) => u.key === lookup.data.uid,
                        );

                        if (existing) {
                            matchingUser = { ...existing, email };
                        }
                    }
                } catch (lookupError) {
                    console.log(
                        "Could not check whether the email is already in use",
                        lookupError,
                    );
                }

                if (matchingUser && matchingUser.role === "Player") {
                    setDuplicateUser(matchingUser);
                    setDuplicateUserDialogIsOpen(true);
                } else {
                    setStatusMessage(errorMessage);
                    setStatusType("error");
                    setStatusIsVisible(true);
                }
            } else {
                setStatusMessage(errorMessage);
                setStatusType("error");
                setStatusIsVisible(true);
            }

            console.log(errorCode + ":" + errorMessage);
        } finally {
            setIsInProgress(false);
            secondaryFirebase.delete().catch(() => {});
        }
    }

    // After save has been pressed
    async function editUser() {
        if (editedProfileFailed) {
            setStatusMessage(
                "This user's details could not be loaded, so saving would " +
                    "overwrite them. Ask a system manager to make the change.",
            );
            setStatusType("error");
            setStatusIsVisible(true);
            return;
        }

        setIsInProgress(true);

        try {
            const nextImageURL = hasChangedAvatar
                ? await uploadAvatar(key)
                : imageURL;

            await editInFirebase(nextImageURL);
            await syncSystemManager(key, user.role, role);
            finishSaving();
            setUser((currentUser) => ({
                ...currentUser,
                firstName,
                lastName,
                phone,
                role,
                permissions,
                imageURL: nextImageURL,
                parentFirstName,
                parentLastName,
                parentEmail,
                parentPhone,
                customRoleName: customRoleName || role,
                admins: nextAdminKeys(),
                under18,
            }));
            setImageURL(nextImageURL);
            setHasChangedAvatar(false);
            setJustCreatedUser(false);
            setSaveConfirmationIsVisible(true);
        } catch (error) {
            const errorMessage = error?.message || "Unable to save user";
            console.error("Unable to save user", error);
            setStatusMessage(errorMessage);
            setStatusType("error");
            setStatusIsVisible(true);
        } finally {
            setIsInProgress(false);
        }
    }

    function editInFirebase(url) {
        const roleName = customRoleName || role;
        const oldFullName = user.firstName + " " + user.lastName;
        const fullName = firstName + " " + lastName;
        const updates = {
            [`users/${key}/firstName`]: firstName,
            [`users/${key}/lastName`]: lastName,
            [`users/${key}/role`]: role,
            [`users/${key}/imageURL`]: url,
            [`users/${key}/customRoleName`]: roleName,
            // Personal data and permissions live only on /userprofiles.
            [`userprofiles/${key}/phone`]: phone,
            [`userprofiles/${key}/permissions`]: permissions,
            [`userprofiles/${key}/parentFirstName`]: parentFirstName,
            [`userprofiles/${key}/parentLastName`]: parentLastName,
            [`userprofiles/${key}/parentPhone`]: parentPhone,
            [`userprofiles/${key}/parentEmail`]: parentEmail,
            [`userprofiles/${key}/under18`]: under18,
            ...buildAdminUpdates({
                folders,
                selectedHubs,
                savedUserKey: key,
                savedAdmins: savedAdminKeys(),
            }),
            ...buildMembershipUpdates({
                folders,
                groups,
                selectedHubs,
                savedUserKey: key,
                draftUserKey: key,
            }),
        };

        if (oldFullName !== fullName) {
            comments.forEach((comment) => {
                if (comment.user === key) {
                    updates[`comments/${comment.key}/author`] = fullName;
                }
            });

            conversations.forEach((conversation) => {
                conversation.messages.forEach((message) => {
                    if (message.user === key) {
                        updates[
                            `conversations/${conversation.key}/messages/${message.key}/fullName`
                        ] = fullName;
                    }
                });
            });
        }

        return firebase.database().ref().update(updates);
    }

    // Whatever the saved user already has. Read from the live list rather than
    // the local draft so a grant made elsewhere since this page loaded is not
    // resolved away.
    function savedAdminKeys() {
        return users.find((u) => u.key === key)?.admins || [];
    }

    function nextAdminKeys() {
        return resolveUserAdmins({
            folders,
            selectedHubs,
            savedUserKey: key,
            savedAdmins: savedAdminKeys(),
        });
    }

    // /systemManagers is the database's own list of who may read and write
    // every user's personal data. Kept in step with the role here so a
    // promotion or demotion does not silently leave it stale.
    //
    // Written SEPARATELY, never folded into the multi-location update above:
    // once the Stage 4 rules make this node console-only the write is
    // refused, and a refusal inside that update would fail the whole save
    // and take the ordinary user edit down with it. On its own it degrades
    // to a warning instead.
    async function syncSystemManager(userKey, previousRole, nextRole) {
        const change = planSystemManagerChange(previousRole, nextRole);

        if (!change) {
            return;
        }

        try {
            await firebase
                .database()
                .ref("systemManagers")
                .child(userKey)
                .set(change === "add" ? true : null);
        } catch (error) {
            console.log("Could not update systemManagers", error);
            setStatusMessage(
                change === "add"
                    ? "Saved, but this user could not be added to the system " +
                      "manager list. Add them in the Firebase console."
                    : "Saved, but this user could not be removed from the " +
                      "system manager list. Remove them in the Firebase console.",
            );
            setStatusType("warning");
            setStatusIsVisible(true);
        }
    }

    function getAdminsObject() {
        return Object.fromEntries(
            nextAdminKeys().map((adminKey) => [adminKey, true]),
        );
    }

    function uploadAvatar(userKey) {
        return new Promise((resolve, reject) => {
            if (user.avatarEditor) {
                let storage = firebase.storage();

                user.avatarEditor
                    .getImageScaledToCanvas()
                    .toBlob(function (blob) {
                        // Upload the new file
                        let avatarRef = storage
                            .ref()
                            .child("avatars/users/" + userKey + "/avatar.png");

                        avatarRef
                            .put(blob)
                            .then((snapshot) => {
                                // Upload was successful
                                console.log("Upload Successful");
                                user.avatar = null;

                                avatarRef.getDownloadURL().then((url) => {
                                    resolve(url);
                                });
                            })
                            .catch((error) => {
                                let errorCode = error.code;
                                let errorMessage = error.message;
                                console.log(errorCode + ":" + errorMessage);
                                setIsInProgress(false);
                                setStatusMessage(error.message);
                                setStatusType("error");
                                setStatusIsVisible(true);
                                reject(error);
                            });
                    });
            } else {
                resolve("none");
            }
        });
    }

    function didChangeAvatar() {
        setHasChangedAvatar(true);
        markUnsavedChanges();
    }

    const handleUnder18CheckboxChange = (event) => {
        setUnder18(event.target.checked);
        markUnsavedChanges();
    };

    function handleEditHubClick() {
        setHubDialogIsOpen(true);
    }

    function handleOpenAccountRecoveryDialog() {
        setAccountRecoveryDialogIsOpen(true);
    }

    function handleCloseAccountRecoveryDialog(success, errorMessage, newEmail) {
        setAccountRecoveryDialogIsOpen(false);

        if (success) {
            setStatusMessage("Account Reset Successfully");
            setStatusType("success");
            setStatusIsVisible(true);
            setEmail(newEmail);
        } else {
            setStatusMessage(errorMessage);
            setStatusType("error");
            setStatusIsVisible(true);
        }
    }

    function handleDismissAccountRecoveryDialog() {
        setAccountRecoveryDialogIsOpen(false);
    }

    function handleCloseHubDialog(newSelectedHubs) {
        setHubDialogIsOpen(false);

        if (!newSelectedHubs) {
            return;
        }

        const previousKeys = selectedHubs
            .map((hub) => hub.key)
            .sort()
            .join("|");
        const nextSelectedHubs = newSelectedHubs.map((hub) =>
            createHubDraft(
                hub,
                groups,
                hubs.find((draft) => draft.key === hub.key),
            ),
        );
        const nextKeys = nextSelectedHubs
            .map((hub) => hub.key)
            .sort()
            .join("|");

        setSelectedHubs(nextSelectedHubs);
        setHubs((current) =>
            current.map(
                (hub) =>
                    nextSelectedHubs.find((draft) => draft.key === hub.key) ||
                    hub,
            ),
        );

        if (previousKeys !== nextKeys) {
            markUnsavedChanges();
        }
    }

    function handleCloseGroupDialog(selectedGroupKeys) {
        if (editingHub && selectedGroupKeys) {
            const selectedKeySet = new Set(selectedGroupKeys);
            const previousKeys = editingHub.groups
                .filter(
                    (group) =>
                        group.name !== "All Users" &&
                        group.users.some((member) => member.key === key),
                )
                .map((group) => group.key)
                .sort()
                .join("|");
            const nextKeys = [...selectedKeySet].sort().join("|");
            const updatedHub = {
                ...editingHub,
                groups: editingHub.groups.map((group) => {
                    if (group.name === "All Users") {
                        return group;
                    }

                    const usersWithoutDraft = group.users.filter(
                        (member) =>
                            member.key !== key && member.key !== "newuser",
                    );

                    return {
                        ...group,
                        users: selectedKeySet.has(group.key)
                            ? [...usersWithoutDraft, { key }]
                            : usersWithoutDraft,
                    };
                }),
            };

            setSelectedHubs((current) =>
                current.map((hub) =>
                    hub.key === updatedHub.key ? updatedHub : hub,
                ),
            );
            setHubs((current) =>
                current.map((hub) =>
                    hub.key === updatedHub.key ? updatedHub : hub,
                ),
            );

            if (previousKeys !== nextKeys) {
                markUnsavedChanges();
            }
        }

        setEditingHub(null);
        setGroupDialogIsOpen(false);
    }

    function handleCloseDuplicateUserDialog() {
        setDuplicateUserDialogIsOpen(false);
    }

    function handleSendPasswordEmailPress() {
        var generateEmailActionLink = firebase
            .functions()
            .httpsCallable("generateEmailActionLink");

        generateEmailActionLink({
            email: user.email,
        })
            .then((result) => {
                let link = result.data.link;
                sendPasswordEmail(link);
            })
            .catch((error) => {
                console.error("Could not generate password email link", {
                    accountEmail: user.email,
                    destinationEmail: email,
                    code: error.code,
                    message: error.message,
                    details: error.details,
                    error,
                });
            });

        setStatusMessage("Password email sent");
        setStatusType("success");
        setStatusIsVisible(true);
    }

    async function handleCloseDeleteDialog(result) {
        if (result !== true || !currentUserData) {
            setConfirmDeleteDialogOpen(false);
            return;
        }

        setDeleteIsInProgress(true);

        // The context only holds the current user's own notifications, so
        // read the node itself. Read before anything is deleted: a failure
        // here costs only the notification entries, which the admin tool's
        // "Deleted user references" can clear later.
        let allNotifications = {};
        try {
            const snapshot = await firebase
                .database()
                .ref("notifications")
                .once("value");
            allNotifications = snapshot.val() || {};
        } catch (error) {
            console.log("Could not read notifications for delete", error);
        }

        const updates = userDeletionUpdates(user.key, {
            users,
            folders,
            groups,
            files,
            notifications: allNotifications,
        });

        // Delete the user from auth first: the callable is where the right to
        // delete them is checked
        try {
            await firebase.functions().httpsCallable("deleteUser")({
                uid: user.uid,
                user: { email: email },
            });
        } catch (error) {
            console.log(error.code + ":" + error.message);
            setStatusMessage("Could not delete this user. Nothing was changed.");
            setStatusType("error");
            setStatusIsVisible(true);
            setDeleteIsInProgress(false);
            setConfirmDeleteDialogOpen(false);
            return;
        }

        // One atomic update: every hub, folder, group, file, admins map,
        // notification and consent entry goes, with the user's own records,
        // or none of it does
        try {
            await firebase.database().ref().update(updates);
        } catch (error) {
            console.log("Could not remove deleted user from the database", error);
            setStatusMessage(
                "The account was deleted, but their hubs, groups and records " +
                    "could not be cleared. Run Deleted user references on the " +
                    "admin page to finish.",
            );
            setStatusType("error");
            setStatusIsVisible(true);
            setDeleteIsInProgress(false);
            setConfirmDeleteDialogOpen(false);
            return;
        }

        // Separately, for the same reason as on save: a deleted person left
        // in /systemManagers keeps a live grant over everyone else's
        // personal data.
        await syncSystemManager(user.key, user.role, null);

        setDeleteIsInProgress(false);
        setConfirmDeleteDialogOpen(false);

        finishSaving();
        history.goBack();
    }

    function handleDeleteButtonPressed() {
        setConfirmDeleteDialogOpen(true);
    }

    function handleSendConsentEmailPress() {
        if (under18) {
            sendConsentEmail(key, "parent");
            setStatusMessage("Consent email sent to parent");
        } else {
            sendConsentAndPasswordEmail(key);

            setStatusMessage("Consent email sent");
            setStatusType("success");
            setStatusIsVisible(true);
        }

        setStatusType("success");
        setStatusIsVisible(true);
    }

    const handleHubSelect = (hub) => () => {
        setEditingHub(hub);
        setGroupDialogIsOpen(true);
    };

    function roleList() {
        if (currentUserData.role === "System Digital Manager") {
            return [
                "System Digital Manager",
                "FAW Team Ops",
                "FAW Analyst",
                "FAW Coach",
                "FAW Support Staff",
                "FAW Coach Educator",
                "FAW Coach Ed Student",
                "FAW Player",
                "FAW Parent",
                "Club Digital Manager",
                "Club Administrator",
                "Club Analyst",
                "Club Coach",
                "Club Coach Educator",
                "Club Coach Ed Student",
                "Club Player",
                "Club Parent",
                "Club Support Staff",
                "Referee",
                "Referee Assessor",
            ];
        } else if (isFAWUser()) {
            return [
                "FAW Team Ops",
                "FAW Analyst",
                "FAW Coach",
                "FAW Support Staff",
                "FAW Coach Educator",
                "FAW Coach Ed Student",
                "FAW Player",
                "FAW Parent",
                "Club Digital Manager",
                "Club Administrator",
                "Club Analyst",
                "Club Coach",
                "Club Coach Educator",
                "Club Coach Ed Student",
                "Club Player",
                "Club Parent",
                "Club Support Staff",
                "Referee",
                "Referee Assessor",
            ];
        } else if (isClubUser()) {
            return [
                "Club Digital Manager",
                "Club Administrator",
                "Club Analyst",
                "Club Coach",
                "Club Coach Educator",
                "Club Coach Ed Student",
                "Club Player",
                "Club Parent",
                "Club Support Staff",
            ];
        } else {
            return ["FAW Player"];
        }
    }

    function isFAWUser() {
        if (
            currentUserData.role === "FAW Team Ops" ||
            currentUserData.role === "FAW Analyst" ||
            currentUserData.role === "FAW Coach" ||
            currentUserData.role === "FAW Coach Educator" ||
            currentUserData.role === "Administrator"
        ) {
            return true;
        }

        return false;
    }

    function isClubUser() {
        if (
            currentUserData.role === "Club Digital Manager" ||
            currentUserData.role === "Club Administrator" ||
            currentUserData.role === "Club Analyst" ||
            currentUserData.role === "Club Coach" ||
            currentUserData.role === "Club Coach Educator"
        ) {
            return true;
        }

        return false;
    }

    // {currentUserData.role ===
    //     "System Digital Manager" && (
    //     <>
    //         <option
    //             value={
    //                 "System Digital Manager"
    //             }
    //         >
    //             System Digital Manager
    //         </option>
    //         <option
    //             value={
    //                 "League Digital Manager"
    //             }
    //         >
    //             League Digital Manager
    //         </option>
    //         <option
    //             value={
    //                 "Club Digital Manager"
    //             }
    //         >
    //             Club Digital Manager
    //         </option>
    //         <option value={"Administrator"}>
    //             Administrator
    //         </option>
    //         <option value={"Analyst"}>
    //             Analyst
    //         </option>
    //         <option
    //             value={"Coach Educator"}
    //         >
    //             Coach Educator
    //         </option>
    //         <option
    //             value={"Coach Ed Student"}
    //         >
    //             Coach Ed Student
    //         </option>
    //     </>
    // )}
    // {currentUserData.role ===
    //     "League Digital Manager" && (
    //     <>
    //         <option
    //             value={
    //                 "League Digital Manager"
    //             }
    //         >
    //             League Digital Manager
    //         </option>
    //         <option
    //             value={
    //                 "Club Digital Manager"
    //             }
    //         >
    //             Club Digital Manager
    //         </option>
    //         <option value={"Administrator"}>
    //             Administrator
    //         </option>
    //         <option value={"Analyst"}>
    //             Analyst
    //         </option>
    //         <option
    //             value={"Coach Educator"}
    //         >
    //             Coach Educator
    //         </option>
    //         <option
    //             value={"Coach Ed Student"}
    //         >
    //             Coach Ed Student
    //         </option>
    //     </>
    // )}
    // {currentUserData.role ===
    //     "Club Digital Manager" && (
    //     <>
    //         <option
    //             value={
    //                 "Club Digital Manager"
    //             }
    //         >
    //             Club Digital Manager
    //         </option>
    //         <option value={"Administrator"}>
    //             Administrator
    //         </option>
    //         <option value={"Analyst"}>
    //             Analyst
    //         </option>
    //         <option
    //             value={"Coach Educator"}
    //         >
    //             Coach Educator
    //         </option>
    //         <option
    //             value={"Coach Ed Student"}
    //         >
    //             Coach Ed Student
    //         </option>
    //     </>
    // )}
    // {currentUserData.role ===
    //     "Administrator" && (
    //     <>
    //         <option value={"Administrator"}>
    //             Administrator
    //         </option>
    //         <option value={"Analyst"}>
    //             Analyst
    //         </option>
    //         <option
    //             value={"Coach Educator"}
    //         >
    //             Coach Educator
    //         </option>
    //         <option
    //             value={"Coach Ed Student"}
    //         >
    //             Coach Ed Student
    //         </option>
    //     </>
    // )}
    // {currentUserData.role === "Analyst" && (
    //     <>
    // <option value={"Analyst"}>
    //     Analyst
    // </option>
    //     </>
    // )}
    // {currentUserData.role ===
    //     "Coach Educator" && (
    //     <>
    //         <option
    //             value={"Coach Educator"}
    //         >
    //             Coach Educator
    //         </option>
    //         <option
    //             value={"Coach Ed Student"}
    //         >
    //             Coach Ed Student
    //         </option>
    //     </>
    // )}
    // <option value={"Coach"}>Coach</option>
    // <option value={"Player"}>Player</option>

    function handleCloseDialogMessage() {
        setMessageDialogOpen(false);
    }

    return (
        <>
            <Prompt
                when={hasUnsavedChanges}
                message={() =>
                    allowNavigationRef.current
                        ? true
                        : unsavedChangesMessage
                }
            />
            {userCanView && (
                <>
                    <Box
                        component="main"
                        sx={{
                            minHeight: "calc(100vh - 64px)",
                            backgroundColor: "background.default",
                            pt: { xs: 2, sm: 3, md: 4 },
                            pb: { xs: 2, sm: 4 },
                        }}
                    >
                        <Container
                            maxWidth="md"
                            sx={{ px: { xs: 1.5, sm: 3 } }}
                        >
                            <Card
                                variant="outlined"
                                sx={{
                                    overflow: "hidden",
                                    borderRadius: 3,
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                    boxShadow: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "0 8px 28px rgba(0, 0, 0, 0.3)"
                                            : "0 8px 28px rgba(20, 35, 25, 0.075)",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        p: { xs: 2, sm: 3 },
                                    }}
                                >
                                    <BackButton />
                                    <Box
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            borderRadius: 2.5,
                                            display: {
                                                xs: "none",
                                                sm: "grid",
                                            },
                                            placeItems: "center",
                                            flexShrink: 0,
                                            color: "primary.main",
                                            backgroundColor: primaryTint,
                                            border: "1px solid",
                                            borderColor: primaryTintBorder,
                                        }}
                                    >
                                        <AccountCircleOutlinedIcon />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            sx={{
                                                fontSize: { xs: 26, sm: 31 },
                                                lineHeight: 1.15,
                                            }}
                                        >
                                            {isEditing
                                                ? "Edit user"
                                                : "New user"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.4,
                                                color: "text.secondary",
                                            }}
                                        >
                                            {isEditing
                                                ? "Update this user’s profile, access and memberships."
                                                : "Create a profile and choose the user’s access."}
                                        </Typography>
                                    </Box>
                                    {userCanDelete && (
                                        <Tooltip title="Delete user">
                                            <IconButton
                                                color="error"
                                                aria-label="Delete user"
                                                onClick={
                                                    handleDeleteButtonPressed
                                                }
                                                sx={{
                                                    flexShrink: 0,
                                                    backgroundColor: errorTint,
                                                    "&:hover": {
                                                        backgroundColor:
                                                            errorHoverTint,
                                                    },
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>

                                <Divider />

                                <CardContent
                                    sx={{
                                        p: { xs: 2.5, sm: 4 },
                                        "&:last-child": {
                                            pb: { xs: 2.5, sm: 4 },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            mb: 3,
                                        }}
                                    >
                                        <AvatarUploadButton
                                            recipient={user}
                                            didChangeAvatar={didChangeAvatar}
                                            path="users"
                                            centered
                                            compact
                                        />
                                        {(hasGivenConsent === false ||
                                            (lastLogin !== "" &&
                                                key !== "newuser")) && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    flexWrap: "wrap",
                                                    gap: 1,
                                                    mt: 1.5,
                                                }}
                                            >
                                                {hasGivenConsent === false && (
                                                    <Chip
                                                        label="Consent form pending"
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {lastLogin !== "" &&
                                                    key !== "newuser" && (
                                                        <Chip
                                                            label={`Last login: ${lastLogin}`}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                color: "text.secondary",
                                                                borderColor:
                                                                    "divider",
                                                            }}
                                                        />
                                                    )}
                                            </Box>
                                        )}
                                    </Box>

                                    <Divider sx={{ mb: 3 }} />

                                    <Box sx={{ mb: 3 }}>
                                        <Typography
                                            variant="h6"
                                            component="h2"
                                        >
                                            User details
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.35,
                                                mb: 2,
                                                color: "text.secondary",
                                            }}
                                        >
                                            Basic contact information for this
                                            account.
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "1fr",
                                                    sm: "1fr 1fr",
                                                },
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                id="firstName"
                                                label="First Name"
                                                variant="outlined"
                                                value={firstName}
                                                onChange={handleChange}
                                                fullWidth
                                            />
                                            <TextField
                                                id="lastName"
                                                label="Last Name"
                                                variant="outlined"
                                                value={lastName}
                                                onChange={handleChange}
                                                fullWidth
                                            />
                                            <TextField
                                                id="email"
                                                label="E-mail"
                                                variant="outlined"
                                                value={email}
                                                onChange={handleChange}
                                                fullWidth
                                                disabled={isEditing}
                                            />
                                            <TextField
                                                id="phone"
                                                label="Mobile Number"
                                                variant="outlined"
                                                value={phone}
                                                onChange={handleChange}
                                                fullWidth
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                mt: 2,
                                                p: 1.5,
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                backgroundColor:
                                                    "background.subtle",
                                            }}
                                        >
                                            <FormControlLabel
                                                sx={{ m: 0 }}
                                                control={
                                                    <Checkbox
                                                        checked={under18}
                                                        onChange={
                                                            handleUnder18CheckboxChange
                                                        }
                                                        name="checkboxThree"
                                                        color="primary"
                                                    />
                                                }
                                                label="This user is under 18"
                                            />
                                        </Box>
                                    </Box>

                                    {under18 && (
                                        <>
                                            <Divider sx={{ mb: 3 }} />
                                            <Box sx={{ mb: 3 }}>
                                                <Typography
                                                    variant="h6"
                                                    component="h2"
                                                >
                                                    Parent, carer or guardian
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mt: 0.35,
                                                        mb: 2,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    Contact details used for
                                                    consent.
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                            xs: "1fr",
                                                            sm: "1fr 1fr",
                                                        },
                                                        gap: 2,
                                                    }}
                                                >
                                                    <TextField
                                                        id="parentFirstName"
                                                        label="First Name"
                                                        variant="outlined"
                                                        value={parentFirstName}
                                                        onChange={handleChange}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        id="parentLastName"
                                                        label="Last Name"
                                                        variant="outlined"
                                                        value={parentLastName}
                                                        onChange={handleChange}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        id="parentEmail"
                                                        label="E-mail"
                                                        variant="outlined"
                                                        value={parentEmail}
                                                        onChange={handleChange}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        id="parentPhone"
                                                        label="Mobile Number"
                                                        variant="outlined"
                                                        value={parentPhone}
                                                        onChange={handleChange}
                                                        fullWidth
                                                    />
                                                </Box>
                                            </Box>
                                        </>
                                    )}

                                    <Divider sx={{ mb: 3 }} />

                                    <Box sx={{ mb: 3 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: {
                                                    xs: "stretch",
                                                    sm: "flex-start",
                                                },
                                                justifyContent:
                                                    "space-between",
                                                flexDirection: {
                                                    xs: "column",
                                                    sm: "row",
                                                },
                                                gap: 2,
                                                mb: 2,
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    component="h2"
                                                >
                                                    Profile and access
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mt: 0.35,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    Assign a role and fine-tune
                                                    permissions.
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                startIcon={<SettingsIcon />}
                                                onClick={
                                                    handleOpenPermissionsDialog
                                                }
                                                sx={{ flexShrink: 0 }}
                                            >
                                                Permissions
                                            </Button>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "1fr",
                                                    sm: "1fr 1fr",
                                                },
                                                gap: 2,
                                            }}
                                        >
                                            <FormControl
                                                variant="outlined"
                                                fullWidth
                                            >
                                                <InputLabel htmlFor="role">
                                                    Role
                                                </InputLabel>
                                                <Select
                                                    native
                                                    value={role}
                                                    onChange={handleChange}
                                                    label="Role"
                                                    inputProps={{
                                                        name: "role",
                                                        id: "role",
                                                    }}
                                                >
                                                    {roleList().map(
                                                        (roleName) => (
                                                            <option
                                                                key={roleName}
                                                                value={roleName}
                                                            >
                                                                {roleName}
                                                            </option>
                                                        ),
                                                    )}
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                id="customRoleName"
                                                label="Custom Role Name (Optional)"
                                                variant="outlined"
                                                fullWidth
                                                value={customRoleName}
                                                onChange={handleChange}
                                            />
                                        </Box>
                                    </Box>

                                    {isEditing && (
                                        <>
                                            <Box
                                                sx={{
                                                    p: { xs: 2, sm: 2.5 },
                                                    mb: 3,
                                                    borderRadius: 2.5,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    backgroundColor:
                                                        "background.subtle",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        mb: 1.75,
                                                    }}
                                                >
                                                    <SecurityOutlinedIcon
                                                        sx={{
                                                            color: "primary.main",
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            Account tools
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            Recovery and account
                                                            email actions.
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                            xs: "1fr",
                                                            sm: "repeat(3, 1fr)",
                                                        },
                                                        gap: 1.25,
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        onClick={
                                                            handleOpenAccountRecoveryDialog
                                                        }
                                                    >
                                                        Account recovery
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={
                                                            <MailOutlineIcon />
                                                        }
                                                        onClick={
                                                            handleSendPasswordEmailPress
                                                        }
                                                    >
                                                        Send password email
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={
                                                            <MailOutlineIcon />
                                                        }
                                                        onClick={
                                                            handleSendConsentEmailPress
                                                        }
                                                    >
                                                        Send consent email
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </>
                                    )}

                                    <Divider sx={{ mb: 3 }} />

                                    <Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: {
                                                    xs: "flex-start",
                                                    sm: "center",
                                                },
                                                justifyContent:
                                                    "space-between",
                                                flexDirection: {
                                                    xs: "column",
                                                    sm: "row",
                                                },
                                                gap: 1.5,
                                                mb: 2,
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    component="h2"
                                                >
                                                    Hubs and groups
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mt: 0.35,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    Choose where this user
                                                    belongs.
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                startIcon={
                                                    <GroupsOutlinedIcon />
                                                }
                                                onClick={handleEditHubClick}
                                                sx={{
                                                    alignSelf: {
                                                        xs: "stretch",
                                                        sm: "center",
                                                    },
                                                }}
                                            >
                                                Edit hubs
                                            </Button>
                                        </Box>

                                        {selectedHubs.length === 0 ? (
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    borderRadius: 2.5,
                                                    textAlign: "center",
                                                    color: "text.secondary",
                                                    border: "1px dashed",
                                                    borderColor: "divider",
                                                    backgroundColor:
                                                        "background.subtle",
                                                }}
                                            >
                                                <GroupsOutlinedIcon
                                                    sx={{
                                                        mb: 0.75,
                                                        color: "primary.main",
                                                    }}
                                                />
                                                <Typography variant="body2">
                                                    This user has not been
                                                    added to any hubs.
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                        "repeat(auto-fill, minmax(130px, 1fr))",
                                                    gap: 1.5,
                                                }}
                                            >
                                                {[...selectedHubs]
                                                    .sort((a, b) => {
                                                        if (
                                                            a.ancestors.length >
                                                            b.ancestors.length
                                                        )
                                                            return 1;
                                                        if (
                                                            a.ancestors.length <
                                                            b.ancestors.length
                                                        )
                                                            return -1;
                                                        return 0;
                                                    })
                                                    .map((hub) => (
                                                        <Tooltip
                                                            key={hub.key}
                                                            title="Click to edit groups"
                                                        >
                                                            <ButtonBase
                                                                onClick={handleHubSelect(
                                                                    hub,
                                                                )}
                                                                sx={{
                                                                    width: "100%",
                                                                    minHeight: 132,
                                                                    p: 1.5,
                                                                    borderRadius: 2.5,
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "column",
                                                                    gap: 1,
                                                                    border: "1px solid",
                                                                    borderColor:
                                                                        "divider",
                                                                    backgroundColor:
                                                                        "background.subtle",
                                                                    "&:hover": {
                                                                        borderColor:
                                                                            primaryHoverBorder,
                                                                        backgroundColor:
                                                                            "action.hover",
                                                                    },
                                                                }}
                                                            >
                                                                <UserAvatar
                                                                    userOrHub={
                                                                        hub
                                                                    }
                                                                    path="hubs"
                                                                    variant="rounded"
                                                                    size="large"
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        width: "100%",
                                                                        fontWeight: 700,
                                                                        overflowWrap:
                                                                            "break-word",
                                                                        display:
                                                                            "-webkit-box",
                                                                        WebkitBoxOrient:
                                                                            "vertical",
                                                                        WebkitLineClamp: 2,
                                                                        overflow:
                                                                            "hidden",
                                                                    }}
                                                                >
                                                                    {hub.name}
                                                                </Typography>
                                                            </ButtonBase>
                                                        </Tooltip>
                                                    ))}
                                            </Box>
                                        )}
                                    </Box>

                                </CardContent>
                            </Card>
                        </Container>
                    </Box>
                    {hasUnsavedChanges && !dialogIsOpen && (
                        <Box
                            role="status"
                            aria-live="polite"
                            sx={{
                                position: "fixed",
                                left: { xs: 12, sm: "auto" },
                                right: { xs: 12, sm: 24 },
                                bottom: { xs: 16, sm: 80 },
                                zIndex: (theme) => theme.zIndex.snackbar - 1,
                                display: "flex",
                                alignItems: { xs: "stretch", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                gap: 1.5,
                                maxWidth: 720,
                                p: 1.5,
                                borderRadius: 2.5,
                                color: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "warning.light"
                                        : "warning.dark",
                                border: "1px solid",
                                borderColor: warningBorder,
                                backgroundColor: warningSurface,
                                boxShadow: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "0 14px 36px rgba(0, 0, 0, 0.45)"
                                        : "0 14px 36px rgba(20, 35, 25, 0.2)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flex: 1,
                                }}
                            >
                                <WarningAmberOutlinedIcon
                                    sx={{ flexShrink: 0 }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 800,
                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                    }}
                                >
                                    Changes haven&apos;t been saved yet
                                </Typography>
                            </Box>
                            <Button
                                variant="text"
                                color="inherit"
                                disabled={isInProgress}
                                onClick={discardChanges}
                                sx={{ flexShrink: 0 }}
                            >
                                Discard changes
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                disabled={isInProgress}
                                onClick={handleSaveClick}
                                startIcon={
                                    isInProgress ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SaveOutlinedIcon />
                                    )
                                }
                                sx={{ flexShrink: 0 }}
                            >
                                {isInProgress
                                    ? "Saving"
                                    : isEditing
                                      ? "Save changes"
                                      : "Create user"}
                            </Button>
                        </Box>
                    )}
                    <Dialog
                        open={saveConfirmationIsVisible}
                        onClose={() => setSaveConfirmationIsVisible(false)}
                        aria-labelledby="save-confirmation-dialog-title"
                        fullWidth
                        maxWidth="xs"
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: 3,
                                    overflow: "hidden",
                                },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                px: { xs: 2, sm: 3 },
                                py: 2.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 2,
                                    display: "grid",
                                    placeItems: "center",
                                    flexShrink: 0,
                                    color: "primary.main",
                                    backgroundColor: "action.hover",
                                }}
                            >
                                <CheckCircleOutlineIcon />
                            </Box>
                            <Typography
                                id="save-confirmation-dialog-title"
                                variant="h5"
                                component="h2"
                                sx={{ flex: 1, minWidth: 0, lineHeight: 1.1 }}
                            >
                                {justCreatedUser
                                    ? "User created"
                                    : "Changes saved"}
                            </Typography>
                            <IconButton
                                aria-label="Dismiss save confirmation"
                                onClick={() =>
                                    setSaveConfirmationIsVisible(false)
                                }
                                sx={{ mt: -0.75, mr: -1 }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <DialogContent
                            dividers
                            sx={{
                                p: { xs: 2, sm: 2.5 },
                                backgroundColor: "background.default",
                            }}
                        >
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.6 }}
                                >
                                    {justCreatedUser
                                        ? "The new user has been created, and their details and access settings have been saved successfully."
                                        : "The user’s details and access settings have been saved successfully."}
                                </Typography>
                            </Box>
                        </DialogContent>

                        <DialogActions
                            sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}
                        >
                            <Button
                                color="inherit"
                                onClick={() =>
                                    setSaveConfirmationIsVisible(false)
                                }
                            >
                                Dismiss
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={goBackToUsers}
                            >
                                Back to users
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <UserPermissionsDialog
                        open={permissionsDialogIsOpen}
                        onClose={handleClosePermissionsDialog}
                        permissions={permissions}
                    />
                    <ManageHubEnrolementDialog
                        open={hubDialogIsOpen}
                        onClose={handleCloseHubDialog}
                        selectedHubs={selectedHubs}
                        hubs={hubs}
                        userKey={key}
                    />
                    <AccountRecoveryDialog
                        open={accountRecoveryDialogIsOpen}
                        onClose={handleCloseAccountRecoveryDialog}
                        dismiss={handleDismissAccountRecoveryDialog}
                        user={user}
                    />
                    {editingHub && (
                        <ManageGroupEnrolementDialog
                            open={groupDialogIsOpen}
                            onClose={handleCloseGroupDialog}
                            hub={editingHub}
                            userKey={key}
                        />
                    )}
                    {duplicateUser && (
                        <DuplicateUserDialog
                            open={duplicateUserDialogIsOpen}
                            onClose={handleCloseDuplicateUserDialog}
                            onSuccess={finishSaving}
                            user={duplicateUser}
                        />
                    )}
                    {confirmDeleteDialogOpen && (
                        <ConfirmDeleteDialog
                            title="Delete User"
                            message="Are you sure you want to delete this user?"
                            open={confirmDeleteDialogOpen}
                            onClose={handleCloseDeleteDialog}
                            inProgress={deleteIsInProgress}
                        />
                    )}
                    {messageDialogOpen && (
                        <DialogWithMessage
                            open={messageDialogOpen}
                            onClose={handleCloseDialogMessage}
                            title={messageDialogTitle}
                            message={messageDialogString}
                        />
                    )}
                </>
            )}
            {userCanView === false && <NoPermissionsMessage />}
            {userCanView === undefined && (
                <Box
                    sx={{
                        minHeight: "calc(100vh - 64px)",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.default",
                    }}
                >
                    <CircularProgress size={32} aria-label="Loading user" />
                </Box>
            )}
            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={handleStatusClose}
            >
                <Alert onClose={handleStatusClose} severity={statusType}>
                    {statusMessage}
                </Alert>
            </Snackbar>
            <ToolbarWithLogo />
        </>
    );
}
